/** Every in-app destination's URL. Builders are the only way views create links, so a
 * tap, a push notification, the inbox and a universal link all open the same screen.
 * Entities are path segments or query parameters; page sections are the hash. */

/** First path segment of every linkable route. trustedRoute and the
 * apple-app-site-association coverage test read this list. */
export const LINK_ROOTS = [
  "onboarding",
  "home",
  "feed",
  "post",
  "person",
  "discuss",
  "profile",
  "manage",
  "review-requests",
  "notifications",
  "notification-preferences",
  "settings",
  "privacy",
] as const;

export type HomeSection = "challenge" | "team" | "following" | "find-people";
export type PersonSection = "week" | "goals" | "activity";
export type SettingsSection = "account" | "season" | "help";
export type ManageSection = "review" | "week" | "challenge" | "team" | "tools";

function build(path: string, query: Record<string, string | undefined | false> = {}, hash?: string) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, value);
  const search = params.toString();
  return `${path}${search ? `?${search}` : ""}${hash ? `#${hash}` : ""}`;
}
const segment = (id: string) => encodeURIComponent(id);

export const links = {
  home: (options: { person?: string; section?: HomeSection; compose?: boolean } = {}) =>
    build("/home", { person: options.person, compose: options.compose && "1" }, options.section),
  person: (userId: string, section?: PersonSection) => build(`/person/${segment(userId)}`, {}, section),
  post: (postId: string, options: { comment?: string } = {}) =>
    build(`/post/${segment(postId)}`, { comment: options.comment }),
  discuss: (options: { topic?: string; thread?: string; reply?: string; search?: string; compose?: boolean } = {}) =>
    build("/discuss", {
      topic: options.topic,
      thread: options.thread,
      reply: options.thread && options.reply,
      search: options.search,
      compose: options.compose && "1",
    }),
  notifications: () => "/notifications",
  notificationPreferences: () => "/notification-preferences",
  settings: (section?: SettingsSection) => build("/settings", {}, section),
  profile: (edit?: string) => build("/profile", { edit }),
  manage: (section?: ManageSection) => build("/manage", {}, section),
  reviewRequests: (application?: string) => build("/review-requests", { application }),
  onboarding: () => "/onboarding",
  /** A captain's team invite: onboarding opens with that team chosen. */
  join: (teamId: string) => build("/onboarding", { team: teamId }),
  privacy: () => "/privacy",
};

/** Shareable https link for a path, on the origin the app is served from. */
export function absoluteLink(path: string, origin = window.location.origin) {
  return new URL(path, origin).toString();
}

/** DOM id for a deep-link target, shared by the element and the scroller. */
export const targetId = {
  person: (userId: string) => `person-${userId}`,
  comment: (commentId: string) => `comment-${commentId}`,
  reply: (replyId: string) => `reply-${replyId}`,
  application: (applicationId: string) => `application-${applicationId}`,
};
